import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, Building2, Users, DollarSign, Save, Eye, KeyRound, CheckCircle2, XCircle } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import api from '../../services/api';
import BillingTab from './BillingTab';

const TenantProfileDialog = ({ open, onClose, tenant }) => {
  const [users, setUsers] = useState([]);
  const [ownerUsers, setOwnerUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [isDemo, setIsDemo] = useState(false);
  const [savingDemo, setSavingDemo] = useState(false);
  const [isFarm, setIsFarm] = useState(false);
  const [savingFarm, setSavingFarm] = useState(false);
  const [isSyndicator, setIsSyndicator] = useState(false);
  const [savingSyndicator, setSavingSyndicator] = useState(false);
  const [features, setFeatures] = useState({});
  const [subscriptionActive, setSubscriptionActive] = useState(false);

  // Keeneland credentials state
  const [keenCreds, setKeenCreds] = useState({ username: '', password: '', email: '' });
  const [savingKeen, setSavingKeen] = useState(false);
  const [testingKeen, setTestingKeen] = useState(false);
  const [keenTestResult, setKeenTestResult] = useState(null);

  useEffect(() => {
    if (open && tenant?.id) {
      fetchTenantData();
    }
  }, [open, tenant?.id]);

  const fetchTenantData = async () => {
    try {
      setLoading(true);
      
      // Fetch fresh tenant data
      const tenantResponse = await api.get(`/tenants/${tenant.id}`);
      const freshTenant = tenantResponse.data;
      
      // Set notes from fresh data
      setNotes(freshTenant.notes || '');
      setIsDemo(freshTenant.is_demo || false);
      setIsFarm(freshTenant.is_farm || false);
      setIsSyndicator(freshTenant.is_syndicator || false);
      setFeatures(freshTenant.features || {});
      setSubscriptionActive(freshTenant.subscription_active || false);
      
      // Fetch users for this tenant
      const usersResponse = await api.get(`/users?tenant_id=${tenant.id}`);
      const allUsers = usersResponse.data || [];
      
      // Separate regular users
      const regularUsers = allUsers.filter(u => !u.roles?.includes('owner'));
      setUsers(regularUsers);

      // Fetch owner portal users linked to this tenant's CRM entities
      try {
        const entitiesRes = await api.get(`/crm-entities?tenant_id=${tenant.id}`);
        const approvedIds = (entitiesRes.data || []).flatMap(e => e.approved_owner_ids || []);
        const pendingIds = (entitiesRes.data || []).flatMap(e => e.pending_owner_ids || []);
        const allOwnerIds = [...new Set([...approvedIds, ...pendingIds])];
        if (allOwnerIds.length > 0) {
          const ownerUsersRes = await api.get('/system-users/owner-users?ids=' + allOwnerIds.join(','));
          setOwnerUsers(ownerUsersRes.data || []);
        } else {
          setOwnerUsers([]);
        }
      } catch { setOwnerUsers([]); }
      
      // Fetch tenant stats
      setStats({
        total_users: allUsers.length || 0,
        total_horses: 0, // TODO: Add horse count
        total_accounts: 0 // TODO: Add account count
      });

      // Fetch Keeneland credentials
      try {
        const keenRes = await api.get(`/repository-sync/admin/keeneland-credentials/${tenant.id}`);
        const k = keenRes.data || {};
        setKeenCreds({
          username: k.username || '',
          password: k.password || '',
          email:    k.email    || '',
        });
      } catch { /* no credentials yet */ }
      setKeenTestResult(null);
    } catch (error) {
      console.error('Error fetching tenant data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveKeeneland = async () => {
    setSavingKeen(true);
    try {
      await api.put(`/repository-sync/admin/keeneland-credentials/${tenant.id}`, {
        ...keenCreds,
        enabled: true,   // always enable when credentials are saved
      });
      toast.success('Keeneland credentials saved');
      setKeenTestResult(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save credentials');
    } finally { setSavingKeen(false); }
  };

  const handleTestKeeneland = async () => {
    setTestingKeen(true);
    setKeenTestResult(null);
    try {
      const res = await api.post(`/repository-sync/admin/test-keeneland/${tenant.id}`);
      setKeenTestResult(res.data);
      if (res.data.email && !keenCreds.email) {
        setKeenCreds(prev => ({ ...prev, email: res.data.email }));
      }
    } catch (e) {
      setKeenTestResult({ success: false, error: e?.response?.data?.detail || 'Connection failed' });
    } finally { setTestingKeen(false); }
  };

  const handleImpersonateUser = async (targetUser) => {
    try {
      const response = await api.post(`/impersonation/start-user/${targetUser.id}`);
      const impersonationData = response.data;

      // Store impersonation state (same pattern as tenant impersonation)
      localStorage.setItem('impersonation_id', impersonationData.impersonation_id);
      localStorage.setItem('original_user', JSON.stringify(JSON.parse(localStorage.getItem('user'))));
      localStorage.setItem('original_token', localStorage.getItem('token'));
      localStorage.setItem('token', impersonationData.access_token);
      localStorage.setItem('user', JSON.stringify(impersonationData.user));

      toast.success(`Now viewing as ${targetUser.email}`);

      // Route to owner portal for owner role, otherwise dashboard
      const isOwner = targetUser.roles?.includes('owner');
      window.location.href = isOwner ? '/owner-portal' : '/dashboard';
    } catch (error) {
      console.error('Error starting user impersonation:', error);
      toast.error(error.response?.data?.detail || 'Failed to start impersonation');
    }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/tenants/${tenant.id}`, { notes });
      toast.success('Notes saved');
      
      // Refresh all tenant data (including notes)
      await fetchTenantData();
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  if (!tenant) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary/10 flex items-center justify-center rounded">
              <Building2 className="w-6 h-6 text-primary" />
            </div>
            <div>
              <DialogTitle>{tenant.name}</DialogTitle>
              <p className="text-sm text-muted-foreground">{tenant.email}</p>
            </div>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <Tabs defaultValue="overview" className="mt-4">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="users">Users</TabsTrigger>
              <TabsTrigger value="billing">Billing</TabsTrigger>
              <TabsTrigger value="keeneland">Keeneland Login</TabsTrigger>
              <TabsTrigger value="notes">Notes</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Users</p>
                        <p className="text-2xl font-bold">{stats.total_users}</p>
                      </div>
                      <Users className="w-6 h-6 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Status</p>
                        <Badge className={tenant.status === 'active' ? 'bg-green-600' : 'bg-yellow-600'}>
                          {tenant.status}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Plan</p>
                        <p className="text-sm font-medium capitalize">{tenant.subscription_tier || 'Starter'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Created</p>
                      <p className="text-sm font-medium">
                        {new Date(tenant.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Tenant ID</p>
                      <p className="text-sm font-mono">{tenant.id}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* FarmFolio */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      FarmFolio
                      {isFarm && <Badge className="bg-green-100 text-green-700">Enabled</Badge>}
                    </div>
                    <Button
                      variant={isFarm ? 'default' : 'outline'}
                      size="sm"
                      disabled={savingFarm}
                      onClick={async () => {
                        setSavingFarm(true);
                        try {
                          await api.put(`/tenants/${tenant.id}`, { is_farm: !isFarm });
                          setIsFarm(!isFarm);
                          toast.success(isFarm ? 'FarmFolio disabled' : 'FarmFolio enabled');
                        } catch { toast.error('Failed to update'); }
                        finally { setSavingFarm(false); }
                      }}
                    >
                      {savingFarm ? 'Saving...' : isFarm ? 'Disable' : 'Enable'}
                    </Button>
                  </CardTitle>
                </CardHeader>
              </Card>

              {/* Syndicator */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      Syndicator
                      {isSyndicator && <Badge className="bg-blue-100 text-blue-700">Enabled</Badge>}
                    </div>
                    <Button
                      variant={isSyndicator ? 'default' : 'outline'}
                      size="sm"
                      disabled={savingSyndicator}
                      onClick={async () => {
                        setSavingSyndicator(true);
                        try {
                          await api.put(`/tenants/${tenant.id}`, { is_syndicator: !isSyndicator });
                          setIsSyndicator(!isSyndicator);
                          toast.success(isSyndicator ? 'Syndicator disabled' : 'Syndicator enabled');
                        } catch { toast.error('Failed to update'); }
                        finally { setSavingSyndicator(false); }
                      }}
                    >
                      {savingSyndicator ? 'Saving...' : isSyndicator ? 'Disable' : 'Enable'}
                    </Button>
                  </CardTitle>
                </CardHeader>
              </Card>

              {/* Demo Tenant Controls */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      Demo Tenant
                      {isDemo && <Badge className="bg-purple-100 text-purple-700">Demo Mode</Badge>}
                    </div>
                    <Button
                      variant={isDemo ? 'default' : 'outline'}
                      size="sm"
                      disabled={savingDemo}
                      onClick={async () => {
                        setSavingDemo(true);
                        try {
                          await api.put(`/tenants/${tenant.id}`, { is_demo: !isDemo });
                          setIsDemo(!isDemo);
                          toast.success(isDemo ? 'Demo mode disabled' : 'Demo mode enabled');
                        } catch { toast.error('Failed to update'); }
                        finally { setSavingDemo(false); }
                      }}
                    >
                      {savingDemo ? 'Saving...' : isDemo ? 'Disable' : 'Enable'}
                    </Button>
                  </CardTitle>
                </CardHeader>
                {isDemo && (
                <CardContent className="space-y-3 pt-0">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Sale Entry Credits</p>
                          <p className="text-xs text-muted-foreground">Add credits without Gumroad purchase</p>
                        </div>
                        <div className="flex gap-1">
                          {[1, 10, 20, 50].map(n => (
                            <Button key={n} variant="outline" size="sm" className="h-7 text-xs" onClick={async () => {
                              try {
                                await api.post(`/billing/demo-add-credits/${tenant.id}`, { credits: n });
                                toast.success(`${n} credits added`);
                              } catch { toast.error('Failed'); }
                            }}>+{n}</Button>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Premium Features</p>
                          <p className="text-xs text-muted-foreground">Toggle features without Gumroad</p>
                        </div>
                        <div className="flex gap-1 flex-wrap">
                          {['branding_control', 'payment_processing', 'unlimited_storage', 'premium_support'].map(f => {
                            const isOn = !!features[f];
                            return (
                              <Button key={f} size="sm"
                                className={`h-7 text-xs ${isOn ? 'bg-primary text-white hover:bg-primary/90' : 'border border-input bg-background hover:bg-accent'}`}
                                variant={isOn ? 'default' : 'outline'}
                                onClick={async () => {
                                  try {
                                    await api.post(`/billing/demo-toggle-feature/${tenant.id}/${f}`);
                                    setFeatures(prev => ({ ...prev, [f]: !prev[f] }));
                                    toast.success(`${f.replace(/_/g, ' ')} ${isOn ? 'disabled' : 'enabled'}`);
                                  } catch { toast.error('Failed'); }
                                }}
                              >
                                {f.split('_')[0]}{isOn ? ' ✓' : ''}
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Subscription</p>
                          <p className="text-xs text-muted-foreground">Activate subscription without payment</p>
                        </div>
                        <Button
                          size="sm"
                          className={`h-7 text-xs ${subscriptionActive ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-primary hover:bg-primary/90 text-white'}`}
                          onClick={async () => {
                            if (subscriptionActive) return;
                            try {
                              await api.post(`/billing/demo-activate/${tenant.id}`);
                              setSubscriptionActive(true);
                              toast.success('Subscription activated');
                            } catch { toast.error('Failed'); }
                          }}
                          disabled={subscriptionActive}
                        >
                          {subscriptionActive ? 'Active ✓' : 'Activate'}
                        </Button>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">Additional Users</p>
                          <p className="text-xs text-muted-foreground">Set number of additional user slots (beyond base 5)</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-xs" onClick={async () => {
                            try {
                              await api.post(`/billing/demo-set-user-slots/${tenant.id}`, { change: -1 });
                              toast.success('User slot removed');
                            } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                          }}>-</Button>
                          <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-xs" onClick={async () => {
                            try {
                              await api.post(`/billing/demo-set-user-slots/${tenant.id}`, { change: 1 });
                              toast.success('User slot added');
                            } catch { toast.error('Failed'); }
                          }}>+</Button>
                        </div>
                      </div>
                </CardContent>
                )}
              </Card>
            </TabsContent>

            {/* Users Tab */}
            <TabsContent value="users" className="space-y-6">
              {/* Regular Users */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Tenant Users ({users.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {users.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">No tenant users</p>
                  ) : (
                    <div className="space-y-2">
                      {users.map(u => (
                        <div key={u.id} className="flex items-center justify-between p-3 border rounded">
                          <div>
                            <p className="font-medium text-sm">{u.first_name} {u.last_name}</p>
                            <p className="text-xs text-muted-foreground">{u.email}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {u.roles?.map(role => (
                              <Badge key={role} variant="outline" className="text-xs">
                                {role.replace('tenant_', '').replace('_', ' ')}
                              </Badge>
                            ))}
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs gap-1"
                              data-testid={`impersonate-tenant-user-${u.id}`}
                              onClick={() => handleImpersonateUser(u)}
                            >
                              <Eye className="w-3 h-3" /> Impersonate
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Owner Users */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Owner Portal Users ({ownerUsers.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {ownerUsers.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">No owner portal users</p>
                  ) : (
                    <div className="space-y-2">
                      {ownerUsers.map(u => (
                        <div key={u.id} className="flex items-center justify-between p-3 border rounded">
                          <div>
                            <p className="font-medium text-sm">{u.first_name} {u.last_name}</p>
                            <p className="text-xs text-muted-foreground">{u.email}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs bg-blue-50">
                              Owner
                            </Badge>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs gap-1"
                              data-testid={`impersonate-owner-user-${u.id}`}
                              onClick={() => handleImpersonateUser({ ...u, roles: ['owner'] })}
                            >
                              <Eye className="w-3 h-3" /> Impersonate
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Billing Tab */}
            <TabsContent value="billing">
              <BillingTab tenant={tenant} />
            </TabsContent>

            {/* Keeneland Login Tab */}
            <TabsContent value="keeneland">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <KeyRound className="w-4 h-4 text-primary" />
                    Keeneland Consignor Portfolio Login
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">
                    Credentials for <strong>flex.keeneland.com/consignorportfolio</strong>. Used by the 10-minute repository sync to import vetting procedure activity.
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label className="text-xs">Username</Label>
                      <Input
                        value={keenCreds.username}
                        onChange={e => setKeenCreds(p => ({ ...p, username: e.target.value }))}
                        placeholder="Enter username"
                        data-testid="keen-username-input"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Password</Label>
                      <Input
                        type="password"
                        value={keenCreds.password}
                        onChange={e => setKeenCreds(p => ({ ...p, password: e.target.value }))}
                        placeholder="••••••••"
                        data-testid="keen-password-input"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Associated Email</Label>
                      <Input
                        value={keenCreds.email}
                        onChange={e => setKeenCreds(p => ({ ...p, email: e.target.value }))}
                        placeholder="Enter associated email"
                        data-testid="keen-email-input"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button onClick={handleSaveKeeneland} disabled={savingKeen} data-testid="keen-save-btn">
                      {savingKeen ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <><Save className="w-4 h-4 mr-2" />Save Credentials</>}
                    </Button>
                    <Button variant="outline" onClick={handleTestKeeneland} disabled={testingKeen || !keenCreds.username} data-testid="keen-test-btn">
                      {testingKeen ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Testing…</> : 'Test Connection'}
                    </Button>
                  </div>

                  {/* Test result */}
                  {keenTestResult && (
                    <div className={`rounded border p-3 text-sm space-y-2 ${keenTestResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                      <div className="flex items-center gap-2 font-medium">
                        {keenTestResult.success
                          ? <><CheckCircle2 className="w-4 h-4 text-green-600" />Connected successfully</>
                          : <><XCircle className="w-4 h-4 text-red-600" />{keenTestResult.error || 'Connection failed'}</>}
                      </div>
                      {keenTestResult.success && (
                        <>
                          {keenTestResult.email && <p className="text-xs text-muted-foreground">Account email: <strong>{keenTestResult.email}</strong></p>}
                          <p className="text-xs text-muted-foreground">{keenTestResult.keeneland_sales_available} Keeneland sales available · {keenTestResult.system_sales_checked} system sales checked</p>
                          {keenTestResult.matches?.length > 0 && (
                            <div className="space-y-1 mt-1">
                              {keenTestResult.matches.map((m, i) => (
                                <div key={i} className={`flex items-start gap-2 text-xs p-1.5 rounded ${m.matched ? 'bg-green-100' : 'bg-amber-100'}`}>
                                  {m.matched
                                    ? <CheckCircle2 className="w-3 h-3 text-green-600 mt-0.5 shrink-0" />
                                    : <XCircle className="w-3 h-3 text-amber-600 mt-0.5 shrink-0" />}
                                  <span>
                                    <strong>{m.system_sale_name}</strong>
                                    {m.matched
                                      ? <> → <span className="text-green-700">{m.keeneland_sale_name}</span>{m.cached && <span className="text-muted-foreground"> (cached)</span>}</>
                                      : <span className="text-amber-700"> — no match found</span>}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Notes Tab */}
            <TabsContent value="notes">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Tenant Notes</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add notes about this tenant..."
                    rows={12}
                    className="resize-none"
                  />
                  <Button onClick={handleSaveNotes} disabled={savingNotes}>
                    {savingNotes ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                    ) : (
                      <><Save className="w-4 h-4 mr-2" /> Save Notes</>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TenantProfileDialog;
